// News Diet Dashboard
import { API_BASE } from './state.js';
import { showToast } from './utils.js';

let dietData = null;

export async function loadNewsDiet() {
  try {
    const res = await fetch(`${API_BASE}/news-diet`, { credentials: 'include' });
    dietData = await res.json();
    return dietData;
  } catch (e) {
    console.warn('Failed to load news diet');
    return null;
  }
}

export function showNewsDietDashboard() {
  if (!dietData) {
    loadNewsDiet().then(data => {
      if (data) showDashboardModal(data);
      else showToast('Could not load your news diet', 'error');
    });
    return;
  }
  showDashboardModal(dietData);
}

function showDashboardModal(data) {
  const existing = document.getElementById('news-diet-modal');
  if (existing) existing.remove();
  
  const modal = document.createElement('div');
  modal.className = 'modal active';
  modal.id = 'news-diet-modal';
  
  const closeModal = () => modal.remove();
  
  if (!data.hasData) {
    modal.innerHTML = `
      <div class="modal-content" style="max-width: 400px; text-align: center;">
        <div class="modal-header">
          <h2>📊 Your News Diet</h2>
          <button class="modal-close">×</button>
        </div>
        <div class="modal-body">
          <div style="padding: 40px 20px;">
            <div style="font-size: 48px; margin-bottom: 16px;">📰</div>
            <p>Start reading stories to see your personalized news diet stats!</p>
          </div>
        </div>
      </div>
    `;
  } else {
    const { stats, categoryBreakdown, topSources, topTopics, readingByHour, feedbackStats } = data;
    
    // Create category bars
    const maxCategory = Math.max(...categoryBreakdown.map(c => c.count), 1);
    const categoryBars = categoryBreakdown.slice(0, 6).map(c => `
      <div class="diet-bar-row">
        <span class="diet-bar-label">${c.category}</span>
        <div class="diet-bar-track">
          <div class="diet-bar-fill" style="width: ${(c.count / maxCategory) * 100}%"></div>
        </div>
        <span class="diet-bar-value">${c.count}</span>
      </div>
    `).join('');
    
    // Create hour heatmap (simplified)
    const hourData = new Array(24).fill(0);
    readingByHour.forEach(h => { hourData[h.hour] = h.count; });
    const maxHour = Math.max(...hourData, 1);
    const peakHour = hourData.indexOf(Math.max(...hourData));
    const peakTime = peakHour < 12 ? `${peakHour || 12}am` : `${peakHour - 12 || 12}pm`;
    
    modal.innerHTML = `
      <div class="modal-content news-diet-modal">
        <div class="modal-header">
          <h2>📊 Your News Diet</h2>
          <button class="modal-close">×</button>
        </div>
        <div class="modal-body">
          <div class="diet-period">${data.period}</div>
          
          <div class="diet-hero">
            <div class="diet-score-circle">
              <span class="diet-score">${stats.engagementScore}</span>
              <span class="diet-score-label">Score</span>
            </div>
            <div class="diet-personality">
              <span class="diet-personality-label">You're a</span>
              <span class="diet-personality-title">${stats.personality}</span>
            </div>
          </div>
          
          <div class="diet-stats-grid">
            <div class="diet-stat">
              <span class="diet-stat-value">${stats.totalReads}</span>
              <span class="diet-stat-label">Stories Read</span>
            </div>
            <div class="diet-stat">
              <span class="diet-stat-value">${stats.daysActive}</span>
              <span class="diet-stat-label">Days Active</span>
            </div>
            <div class="diet-stat">
              <span class="diet-stat-value">${stats.categoriesExplored}</span>
              <span class="diet-stat-label">Categories</span>
            </div>
            <div class="diet-stat">
              <span class="diet-stat-value">${feedbackStats.like || 0}</span>
              <span class="diet-stat-label">Likes Given</span>
            </div>
          </div>
          
          ${categoryBreakdown.length > 0 ? `
            <div class="diet-section">
              <h3>📂 Categories</h3>
              <div class="diet-bars">
                ${categoryBars}
              </div>
            </div>
          ` : ''}
          
          ${topTopics.length > 0 ? `
            <div class="diet-section">
              <h3>🏷️ Top Topics</h3>
              <div class="diet-tags">
                ${topTopics.map(t => `<span class="diet-tag">${t.topic}</span>`).join('')}
              </div>
            </div>
          ` : ''}
          
          ${topSources.length > 0 ? `
            <div class="diet-section">
              <h3>📰 Preferred Sources</h3>
              <div class="diet-tags">
                ${topSources.map(s => `<span class="diet-tag">${s.source}</span>`).join('')}
              </div>
            </div>
          ` : ''}
          
          <div class="diet-section">
            <h3>⏰ Peak Reading Time</h3>
            <p class="diet-peak-time">${peakTime}</p>
          </div>
        </div>
      </div>
    `;
  }
  
  modal.querySelector('.modal-close').onclick = closeModal;
  modal.onclick = (e) => { if (e.target === modal) closeModal(); };
  document.body.appendChild(modal);
}

// Add dashboard button to settings or header
export function addNewsDietButton() {
  const settingsPanel = document.querySelector('.settings-panel');
  if (!settingsPanel || document.getElementById('news-diet-btn')) return;
  
  const btn = document.createElement('button');
  btn.id = 'news-diet-btn';
  btn.className = 'settings-btn';
  btn.innerHTML = '📊 Your News Diet';
  btn.onclick = showNewsDietDashboard;
  settingsPanel.appendChild(btn);
}

// Styles
const style = document.createElement('style');
style.textContent = `
  .news-diet-modal {
    max-width: 500px;
  }
  .diet-period {
    text-align: center;
    color: var(--text-secondary);
    font-size: 12px;
    margin-bottom: 20px;
  }
  .diet-hero {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 30px;
    margin-bottom: 24px;
  }
  .diet-score-circle {
    width: 100px;
    height: 100px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--accent-color), #6366f1);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: white;
  }
  .diet-score {
    font-size: 32px;
    font-weight: bold;
    line-height: 1;
  }
  .diet-score-label {
    font-size: 11px;
    opacity: 0.8;
  }
  .diet-personality {
    text-align: left;
  }
  .diet-personality-label {
    display: block;
    font-size: 12px;
    color: var(--text-secondary);
  }
  .diet-personality-title {
    font-size: 24px;
    font-weight: 600;
  }
  .diet-stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-bottom: 24px;
  }
  .diet-stat {
    text-align: center;
    padding: 12px 8px;
    background: var(--bg-secondary);
    border-radius: 8px;
  }
  .diet-stat-value {
    display: block;
    font-size: 24px;
    font-weight: bold;
    color: var(--accent-color);
  }
  .diet-stat-label {
    font-size: 10px;
    color: var(--text-secondary);
  }
  .diet-section {
    margin-bottom: 20px;
  }
  .diet-section h3 {
    font-size: 14px;
    margin-bottom: 12px;
    color: var(--text-secondary);
  }
  .diet-bars {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .diet-bar-row {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .diet-bar-label {
    width: 80px;
    font-size: 12px;
    text-transform: capitalize;
  }
  .diet-bar-track {
    flex: 1;
    height: 8px;
    background: var(--bg-secondary);
    border-radius: 4px;
    overflow: hidden;
  }
  .diet-bar-fill {
    height: 100%;
    background: linear-gradient(90deg, var(--accent-color), #6366f1);
    border-radius: 4px;
    transition: width 0.5s ease;
  }
  .diet-bar-value {
    width: 30px;
    text-align: right;
    font-size: 12px;
    color: var(--text-secondary);
  }
  .diet-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .diet-tag {
    padding: 6px 12px;
    background: var(--bg-secondary);
    border-radius: 20px;
    font-size: 12px;
    text-transform: capitalize;
  }
  .diet-peak-time {
    font-size: 28px;
    font-weight: bold;
    text-align: center;
    color: var(--accent-color);
  }
`;
document.head.appendChild(style);

window.appShowNewsDiet = showNewsDietDashboard;
